import { type ReactNode } from 'react'
import { match } from '@/utils/pattern-match-util'
import {
  ClickableTable,
  ClickableTableBody,
  ClickableTableHead,
  ClickableTableHeader,
} from '@/components/Table/ClickableTable'
import type { TableSortOrder } from '@/components/Table'
import { Checkbox } from '@/components/Checkbox'
import { type ColumnId } from './staffing-table-columns'
import styles from './StaffingTable.module.css'

export interface StaffingTableColumn {
  id: ColumnId
  header: string
  align: 'left' | 'center' | 'right'
  canSort: boolean
  sortOrder: TableSortOrder
  onSort: (sortOrder: TableSortOrder) => void
}

export interface StaffingTableProps {
  columns: StaffingTableColumn[]
  allSelectedState: boolean | 'indeterminate'
  onToggleAll: () => void
  /**
   * The infinite scroll loader, rendered below the table while more postings can be fetched.
   */
  loader?: ReactNode
  /**
   * A `StaffingTableRow` per posting.
   */
  children?: ReactNode
}

export function StaffingTable({
  columns,
  allSelectedState,
  onToggleAll,
  loader,
  children,
}: StaffingTableProps) {
  return (
    <>
      <ClickableTable striped>
        <ClickableTableHead>
          {columns.map((column) =>
            column.id === 'Checkbox' ? (
              <ClickableTableHeader
                key={column.id}
                className={columnClassName(column.id)}
                alignment={column.align}
              >
                <Checkbox
                  onChange={onToggleAll}
                  checked={allSelectedState}
                  testId="staffing-table-select-all"
                />
              </ClickableTableHeader>
            ) : (
              <ClickableTableHeader
                key={column.id}
                className={columnClassName(column.id)}
                canSort={column.canSort}
                sortOrder={column.sortOrder}
                onSort={column.onSort}
                alignment={column.align}
              >
                {column.header}
              </ClickableTableHeader>
            ),
          )}
        </ClickableTableHead>
        <ClickableTableBody>{children}</ClickableTableBody>
      </ClickableTable>
      {loader}
    </>
  )
}

function columnClassName(id: ColumnId) {
  return match(id, {
    Checkbox: styles.checkboxColumn,
    Workspace: styles.workspaceColumn,
    OpportunityType: styles.opportunityTypeColumn,
    Location: styles.locationColumn,
    PrimarySoftware: styles.primarySoftwareColumn,
    Backup: styles.backupColumn,
    SupplementalSoftware: styles.supplementalSoftwareColumn,
    Specialties: styles.specialtiesColumn,
    TypeOfWork: styles.typeOfWorkColumn,
    ProsRequired: styles.prosRequiredColumn,
    MaxConcurrentWorkspaces: styles.maxConcurrentWorkspacesColumn,
    Status: styles.statusColumn,
    Action: styles.actionColumn,
  })
}
